import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import clientConfig from 'config';
import moment from 'moment';

// ------------------------------------------------
// CSRF Token Generation

const genToken = function (a?: any): string {
  return a ? (a ^ ((Math.random() * 16) >> (a / 4))).toString(16) : ([1e16] + (1e16 as any)).replace(/[01]/g, genToken);
};

// Requests sent in a short timeframe have to share same csrf token since document.cookie is used
// for storing the csrf token for the cookie, and document.cookie is global.
const timeboxedTokenFactory = () => {
  let token = genToken();
  let genTokenTimeout: ReturnType<typeof setTimeout> | null = null;
  return () => {
    if (genTokenTimeout) clearTimeout(genTokenTimeout);
    genTokenTimeout = setTimeout(() => (token = genToken()), 250);
    return token;
  };
};
const timeboxedToken = timeboxedTokenFactory();

// ------------------------------------------------
// Failed Request Ring Buffer (for error boundary diagnostics)

interface FailedRequestInfo {
  url: string;
  method: string;
  status: number | null;
  timestamp: string;
  responseBody?: string;
}

const failedRequests: FailedRequestInfo[] = [];
const MAX_FAILED_REQUESTS = 5;

function recordFailure(info: FailedRequestInfo) {
  failedRequests.push(info);
  if (failedRequests.length > MAX_FAILED_REQUESTS) failedRequests.shift();
}

export function getRecentFailedRequests(): FailedRequestInfo[] {
  return [...failedRequests];
}

// ------------------------------------------------
// Types

interface RequestConfig {
  onSlow?: (() => void) | null;
  relativeUrls?: boolean;
  baseUrl?: string | null;
  rootUrl?: string;
  authToken?: string | null;
  headers?: { header: string; value: string }[];
  auth?: { loginUrl?: string };
  [key: string]: any;
}

interface RequestResponse {
  status: number;
  data: any;
  headers: {
    Location: string | null;
    Allow: string | null;
    'content-type': string | null;
  };
}

interface RequestOptions {
  expectBinary?: boolean;
  [key: string]: any;
}

// Per-call fields we stash on the axios config so interceptors can read them.
type GabyRequestConfig = AxiosRequestConfig & { _csrfToken?: string | null };

// The statuses treated as success. Note: deliberately NOT `status < 300` —
// only these exact codes resolve; anything else (incl. 203/205/206) rejects.
const OK_STATUSES = new Set([200, 201, 202, 204]);

// ------------------------------------------------
// Axios instance

// Exported for test access only — do not use directly in application code
export const axiosInstance: AxiosInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/hal+json, application/json',
  },
  // Don't throw on non-2xx — the response interceptor decides success itself.
  validateStatus: () => true,
});

// ------------------------------------------------
// Request object (preserves original public API)

export const request = {
  config: {
    onSlow: function () {} as (() => void) | null,
    relativeUrls: true,
    baseUrl: null as string | null,
    authToken: null as string | null,
  } as RequestConfig,

  init: function (config: RequestConfig) {
    this.config = config;
  },

  setToken: function setToken(token: string) {
    this.config.authToken = token;
  },

  _doRequest: function (
    method: string,
    url: string,
    data?: any,
    csrfToken?: string | null,
    onProgress?: ((progress: number) => void) | null,
    options: RequestOptions = {}
  ): Promise<RequestResponse> {
    const axiosConfig: GabyRequestConfig = {
      method: method as any,
      url,
      // Don't throw on non-2xx — the response interceptor checks status itself.
      validateStatus: () => true,
      // Read by the request interceptor to pick a caller-provided CSRF token.
      _csrfToken: csrfToken ?? null,
    };

    // Binary response support
    if (options.expectBinary) {
      axiosConfig.responseType = 'blob';
    }

    // Upload progress tracking
    if (onProgress) {
      axiosConfig.onUploadProgress = function (e) {
        if (e.total) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }

    // Request body
    if (data) {
      axiosConfig.data = data;
    }

    // onSlow ticker — fires every 4s while the request is in flight.
    let timeoutId: ReturnType<typeof setInterval> | undefined;
    if (this.config.onSlow) {
      timeoutId = setInterval(this.config.onSlow, 4000);
    }

    return axiosInstance
      .request(axiosConfig)
      .then((response: AxiosResponse) => {
        // Only successful responses reach here — the response interceptor
        // rejects non-OK statuses and network errors.
        const responseHeaders = {
          Location: (response.headers['location'] as string | undefined) || null,
          Allow: (response.headers['allow'] as string | undefined) || null,
          'content-type': (response.headers['content-type'] as string | undefined) || null,
        };
        return { status: response.status, data: response.data, headers: responseHeaders };
      })
      .finally(() => {
        if (timeoutId) clearInterval(timeoutId);
      });
  },

  generateToken: genToken,

  get: function (url: string, options: RequestOptions = {}): Promise<RequestResponse> {
    return this._doRequest('GET', url, null, null, null, options);
  },
  post: function (
    url: string,
    data?: any,
    csrfToken?: string | null,
    onProgress?: ((progress: number) => void) | null,
    options: RequestOptions = {}
  ): Promise<RequestResponse> {
    return this._doRequest('POST', url, data, csrfToken, onProgress, options);
  },
  patch: function (url: string, data?: any, csrfToken?: string | null): Promise<RequestResponse> {
    return this._doRequest('PATCH', url, data, csrfToken);
  },
  put: function (url: string, data?: any, csrfToken?: string | null): Promise<RequestResponse> {
    return this._doRequest('PUT', url, data, csrfToken);
  },
  del: function (url: string, data?: any, csrfToken?: string | null): Promise<RequestResponse> {
    return this._doRequest('DELETE', url, data, csrfToken);
  },

  appendQueryParams: function (url: string, queryparams?: Record<string, any>): string {
    if (!queryparams || Object.keys(queryparams).length === 0) {
      return url;
    }
    let paramsString = '?';
    for (const [key, value] of Object.entries(queryparams)) {
      const lastChar = paramsString.charAt(paramsString.length - 1);
      if (lastChar !== '&' && lastChar !== '?') {
        paramsString += '&';
      }
      paramsString += `${key}=${value}`;
    }
    return url + paramsString;
  },
};

// ------------------------------------------------
// Interceptors — all the cross-cutting middleware lives here.

// Request: URL building + CSRF double-submit cookie (mutating methods only).
axiosInstance.interceptors.request.use((config) => {
  const cfg = config as GabyRequestConfig;

  // If the URL starts with http assume it's the full thing, otherwise add the root ourselves.
  let url = cfg.url || '';
  if (url.slice(0, 4) !== 'http') {
    let rootUrl = request.config.rootUrl;
    if (request.config.relativeUrls) {
      // Build the URL out of the current page URL
      rootUrl = window.location.protocol + '//' + window.location.host;
    }
    url = rootUrl + url;
  }
  cfg.url = url;

  // Double submit cookie pattern for CSRF
  const method = (cfg.method || '').toUpperCase();
  if (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
    const token = cfg._csrfToken ? cfg._csrfToken : timeboxedToken();
    cfg.headers = cfg.headers ?? {};
    (cfg.headers as any)['X-CSRF-TOKEN'] = token;
    document.cookie = 'CSRF-TOKEN=' + token + '; SameSite=Strict; path=/';
  }

  return config;
});

// Response: success/error resolution + 401 redirect + ring buffer + session timer.
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    // validateStatus:()=>true routes every non-network response here.
    if ((clientConfig as any)?.useSessionTimeoutNotification) {
      refreshSessionTimeoutTimer();
    }

    if (OK_STATUSES.has(response.status)) {
      return response;
    }

    // 401 — redirect to login page
    if (response.status === 401) {
      window.location.pathname = request.config.auth?.loginUrl || '/';
    }

    console.error(
      'Request failed',
      response.status,
      (response.config as GabyRequestConfig).responseType === 'blob' ? '(binary response)' : response.data,
      response.config.url
    );

    // Record in ring buffer for error boundary diagnostics
    recordFailure({
      url: response.config.url || '',
      method: (response.config.method || '').toUpperCase(),
      status: response.status,
      timestamp: new Date().toISOString(),
      responseBody: typeof response.data === 'string' ? response.data.substring(0, 500) : undefined,
    });

    return Promise.reject({ status: response.status, request: response });
  },
  (error: AxiosError) => {
    // Network error or request cancelled (no response)
    console.error('Request failed', error.message, error.config?.url);

    const status = error.response?.status || 0;

    recordFailure({
      url: error.config?.url || '',
      method: (error.config?.method || '').toUpperCase(),
      status,
      timestamp: new Date().toISOString(),
    });

    return Promise.reject({ status, request: error.response || error });
  }
);

export default request;

// ------------------------------------------------
// Session Timeout

let callback: ((endTime: any) => void) | undefined;
export function setTimeoutCallback(timeoutCallback: (endTime: any) => void) {
  callback = timeoutCallback;
}

// to avoid race conditions between setTimeout and setTimeoutCallback we dont call callback directly
function onTimeout(endTime: any) {
  if (callback) callback(endTime);
}

// will call callback when its time to show warning (default 5 minutes before session timeout)
let intervalId: ReturnType<typeof setInterval> | undefined;
export function refreshSessionTimeoutTimer() {
  if (!clientConfig) {
    return; // we need to wait until config has been fetched so we know timeout time
  }
  const sessionTimeoutTimeInMinutes = (clientConfig as any).sessionTokenTTLinSeconds / 60 || 120;
  const minutesToWarnBeforeSessionTimeout = (clientConfig as any).minutesBeforeShowingTimeoutWarning || 5;
  if (intervalId) {
    clearInterval(intervalId);
  }
  const warningTime = moment().add(sessionTimeoutTimeInMinutes - minutesToWarnBeforeSessionTimeout, 'minutes');
  const timeOutTime = moment().add(sessionTimeoutTimeInMinutes, 'minutes');
  intervalId = setInterval(() => {
    if (moment().isSameOrAfter(warningTime)) {
      onTimeout(timeOutTime);
      clearInterval(intervalId);
    }
  }, 1000);
}
